import styled, { css, keyframes } from 'styled-components'
import { Button, IconRegularChevronDown, theme } from '@notino/react-styleguide'
import Collapse from 'rc-collapse'

// styles
import { NotinoLabelLarge, NotinoLabelRegular400, NotinoLabelSmall400, resetButtonStyles } from '../../../styles/helpers'
import { RESERVATION_BOOKING_MODAL_MOBILE_BREAKPOINT } from '../../../styles/constants'

export const Form = styled.form``

export const CardWrapper = styled.div<{ $collapsible: boolean }>`
	background: ${theme.color.background.tertiary};
	padding: 16px 20px;
	cursor: ${({ $collapsible }) => ($collapsible ? 'pointer' : 'default')};
`
export const Divider = styled.div`
	border-top: 1px solid rgba(225, 225, 225, 1);
	width: 100%;
	margin-top: 16px;
	margin-bottom: 16px;
`

export const LoadingText = styled.div``

export const EmployeeCollapseWrapper = styled(Collapse)<{ $collapsible: boolean }>`
	display: flex;
	flex-direction: column;
	gap: 8px;
	margin: -20px -20px 16px -20px;

	.rc-collapse-motion {
		transition:
			height 150ms,
			opacity 150ms;
	}

	.rc-collapse-item {
		.rc-collapse-header {
			cursor: ${({ $collapsible }) => ($collapsible ? 'pointer' : 'default')};

			.rc-collapse-header-text {
				${NotinoLabelRegular400};
				width: 100%;
			}
			.rc-collapse-expand-icon {
				display: none;
			}
		}

		.rc-collapse-content {
			&.rc-collapse-content-hidden {
				display: none;
			}
		}
	}

	@media (min-width: ${RESERVATION_BOOKING_MODAL_MOBILE_BREAKPOINT}) {
		margin: -24px -24px 16px -24px;
	}
`

export const TimeSlotsCollapseWrapper = styled(Collapse)`
	display: flex;
	flex-direction: column;
	gap: 8px;

	.rc-collapse-motion {
		transition:
			height 150ms,
			opacity 150ms;
	}

	.rc-collapse-item {
		margin-bottom: 8px;
		.rc-collapse-header {
			display: flex;
			gap: 2px;
			align-items: center;
			cursor: pointer;

			.rc-collapse-header-text {
				order: 1;
				${NotinoLabelRegular400}
			}
			.rc-collapse-expand-icon {
				order: 2;
			}
		}

		.rc-collapse-content {
			&.rc-collapse-content-hidden {
				display: none;
			}
		}
	}
`

export const ChevronIcon = styled(IconRegularChevronDown)<{ $isActive?: boolean }>`
	transform: rotate(${({ $isActive }) => ($isActive ? '180deg' : '0deg')});
`

export const CollapseContent = styled.div`
	padding-top: 8px;
	display: flex;
	flex-direction: column;
	gap: 8px;
`
export const EmployeeTitle = styled.div`
	${NotinoLabelRegular400}
`

export const EmployeeDescription = styled.div`
	${NotinoLabelSmall400};
	color: ${theme.color.text.tertiary};
	margin-bottom: 8px;
`

export const PriceAndDurationWrapper = styled.div`
	display: flex;
	flex-direction: column;
	color: ${theme.color.text.secondary};
`

export const DurationWrapper = styled.div`
	display: flex;
	align-items: center;
	gap: 4px;
`

export const TimeSlotEmployeeWrapper = styled.div`
	margin: 8px;
`

export const EmployeeOptionWrapper = styled.div<{ $isDisabled: boolean; $isTimeSlotEmployee: boolean }>`
	border-bottom: 1px solid ${theme.color.border.divider};
	cursor: pointer;

	${({ $isTimeSlotEmployee }) =>
		$isTimeSlotEmployee
			? css`
					padding: 16px 0;
				`
			: css`
					padding: 16px 20px;
					@media (min-width: ${RESERVATION_BOOKING_MODAL_MOBILE_BREAKPOINT}) {
						padding: 16px 24px;
					}
				`};

	${({ $isDisabled }) =>
		$isDisabled &&
		css`
			cursor: default;
			opacity: 0.5;
		`}
`

export const DayPeriodEmptyState = styled.div`
	${NotinoLabelRegular400};
	color: ${theme.color.text.tertiary};
	text-align: center;
`

export const EmptyStateLinkButton = styled.button`
	${resetButtonStyles};
	${NotinoLabelRegular400};
	color: ${theme.color.text.secondary};
	text-decoration: underline;

	&:hover {
		text-decoration: none;
	}
`

export const FooterWrapper = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 10px;
`

export const DateTimeInfo = styled.div``

export const SlotsRegion = styled.div``

export const SelectedDate = styled.div`
	${NotinoLabelLarge};
	color: ${theme.color.text.primary};
	text-transform: capitalize;
`

export const SlotWrapper = styled.div`
	display: flex;
	flex-direction: column;
	gap: 12px;
	&:not(:last-child) {
		margin-bottom: 16px;
	}
`

export const SelectedTimeSlot = styled.div`
	${NotinoLabelRegular400};
	color: ${theme.color.text.tertiary};
	height: 1.25rem;
`

export const ConfirmButton = styled(Button)`
	margin-left: auto;
`

const groupSlotSkeletonShimmer = keyframes`
	0% {
		transform: translateX(-100%);
	}
	100% {
		transform: translateX(100%);
	}
`

const groupSlotSkeletonShimmerStyles = css`
	position: relative;
	overflow: hidden;
	background-color: ${theme.color.background.tertiary};

	&::after {
		content: '';
		position: absolute;
		inset: 0;
		background: linear-gradient(
			90deg,
			transparent 0%,
			rgba(255, 255, 255, 0) 20%,
			rgba(255, 255, 255, 0.85) 50%,
			rgba(255, 255, 255, 0) 80%,
			transparent 100%
		);
		animation: ${groupSlotSkeletonShimmer} 1.2s ease-in-out infinite;
	}
`

export const DateHeaderSkeleton = styled.div`
	height: 1.25rem;
	width: 38%;
	border-radius: 0;
	${groupSlotSkeletonShimmerStyles}
`

export const SlotCardSkeleton = styled.div`
	position: relative;
	display: flex;
	align-items: center;
	min-height: 92px;
	padding: 20px;
	border: 1px solid ${theme.color.border.divider};
	border-radius: 4px;
	background: ${theme.color.common.white};
`

export const SlotCardSkeletonContent = styled.div`
	display: flex;
	flex-direction: column;
	gap: 10px;
	width: 100%;
	padding-right: 36px;
`

export const SlotTimeSkeleton = styled.div`
	height: 1.125rem;
	width: 28%;
	border-radius: 0;
	${groupSlotSkeletonShimmerStyles}
`

export const SlotFooterSkeleton = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
`

export const SlotMetaSkeleton = styled.div<{ $width?: string }>`
	height: 0.875rem;
	width: ${({ $width }) => $width || '35%'};
	border-radius: 0;
	${groupSlotSkeletonShimmerStyles}
`

export const SlotRadioSkeleton = styled.div`
	position: absolute;
	top: 50%;
	right: 20px;
	width: 20px;
	height: 20px;
	border-radius: 50%;
	transform: translateY(-50%);
	${groupSlotSkeletonShimmerStyles}
`
